/**
 * Lib.Common: v-num directive
 *
 * @file
 * Sets an element's `textContent` with a number, using local formatting. The modifiers include all of the
 * [$num](#/global/num) formatters. However, using a directive bypasses the Vue render function and can be faster when
 * rendering a large table of information frequently. We also allow visual adjustments that are not possible in a text
 * formatter.
 *
 * Default behavior is equivalent to `$num.decimal`; it will render unlimited decimal places.
 *
 * @modifier raw - Preformatted number; inserts direction codes in RTL languages or displays emdash if `undefined`
 * @modifier bytes - In kB, MB, GB, or TB (or local equivalent) as appropriate, with at least 2 significant digits.
 * @modifier int - Integer portion only
 * @modifier fixed - Fixed point number, with 2 decimal places by default; number of places from 0 to 8 may be specified via dynamic argument: `v-num:[8].fixed="value"`
 * @modifier percent - Fixed point percentage, with 2 decimal places by default; number of places from 0 to 4 may be specified via dynamic argument: `v-num:[0].percent="value"`
 * @modifier ticks - 1/32 ticks (referred to internally as Garban pricing) with three places right of apostrophe separator.
 * @modifier fraction - Fractional value with least possible denominator; specify denominator via dynamic argument.
 * @modifier flat - Omit thousands/lakh/crore grouping
 * @modifier color - If the value is nonzero, apply `positive` or `negative` (green or red) foreground coloring.
 * @modifier sign - If the value is nonzero, display a positive or negative sign.
 * @modifier flex - Strip the decimal places if the number is 1 million or higher, to conserve space.
 * @modifier compact - Avoid ever displaying more than six digits by stripping decimal places or applying an abbreviation as necessary.
 * @modifier smallFraction - Enclose the part of the number right of the decimal point in a `<small>` tag.
 * @modifier smallPercent - Enclose the percent sign in a `<small>` tag.
 * @modifier smallCurrency - Enclose the currency symbol in a `<small>` tag.
 * @modifier smallAbbrev - Enclose the abbreviation (for compact formatting) in a `<small>` tag.
 *
 * @param {number} - If `fixed` or `percent`, the number of decimal places to render. If `fraction`, the denominator.
 * @value {boolean} - The number to render.
 */


import $num from '../global/num';
import { dirty } from '../global/num';


const emDash = '—';


function update(el, binding) {
    const value = binding.value;
    const state = el.__num__;

    if (state.arg !== binding.arg || state.d !== dirty) {
        state.ref--;
        bind(el, binding);
    } else if (value !== state.val) {
        const html = state.fn(value);
        const nan = html === emDash;
        if (nan != state.nan) {
            if (nan) {
                el.classList.add('_nan');
            } else {
                el.classList.remove('_nan');
            }
        }
        if (binding.modifiers.color) {
            const ncl = state.cls;
            const cls = state.get(value);
            if (ncl !== cls) {
                if (ncl) {
                    el.classList.remove(ncl);
                }
                if (cls) {
                    el.classList.add(cls);
                }
                state.cls = cls;
            }
        }
        if (el.__privacy__?.cache !== void 0) {
            el.__privacy__.html = html;
        } else {
            el.innerHTML = html;
        }
        state.val = value;
        state.nan = nan;
    }
}

function bind(el, binding) {
    const mod = binding.modifiers;
    const smallFraction = mod.smallFraction;
    const smallPercent = mod.smallPercent;
    const smallCurrency = mod.smallCurrency;
    const smallAbbrev = mod.smallAbbrev;

    let fn =
        mod.raw         ? $num.raw :
        mod.bytes       ? $num.bytes :
        mod.int         ? $num.int :
        mod.fixed       ? binding.arg ? $num['fixed' + binding.arg] : $num.fixed :
        mod.percent     ? binding.arg ? $num['percent' + binding.arg] : $num.percent :
        mod.ticks       ? $num.ticks :
        mod.fraction    ? value => $num.fraction(value, binding.arg) :
                          $num.decimal;

    if (mod.flex && fn.flex) {
        fn = fn.flex;
    } else if (mod.compact && fn.compact) {
        fn = fn.compact;
    }
    if (mod.flat && fn.flat) {
        fn = fn.flat;
    } else if (mod.sign && fn.sign) {
        fn = fn.sign;
    }

    if (smallFraction || smallPercent || smallCurrency || smallAbbrev || mod.compact) {
        const parts = fn.parts;
        fn = function(num) {
            let p = parts(num);
            let r = '';
            for (let i = 0; i < p.length; i++) {
                let value = p[i].value;
                let type = p[i].type;
                if (
                    type == 'fraction' && smallFraction ||
                    type == 'percentSign' && smallPercent ||
                    type == 'currency' && smallCurrency ||
                    type == 'compact' && (smallAbbrev || mod.compact && value.length > 1)) {
                    r += '<small>' + value + '</small>';
                } else {
                    r += value;
                }
            }
            return r;
        }
    }

    if (el.__num__ === void 0) {
        el.__num__ = {
            ref: 0,
            nan: false,
            cls: null,
            get: mod.raw
                ? value => /^-/.test(value) ? '_nneg' : /[1-9]/.test(value) ? '_npos' : void 0
                : value => value < 0 ? '_nneg' : value > 0 ? '_npos' : void 0
        };
    }
    const data = el.__num__;
    data.ref++;
    data.fn = fn;
    data.val = NaN;
    data.arg = binding.arg;
    data.d = dirty;
    update(el, binding);
}

function unbind(el) {
    if (!--el.__num__.ref) {
        el.replaceChildren();
        el.classList.remove('_nan', '_nneg', '_npos');
        delete el.__num__;
    }
}


export default {
    name: 'num',
    directive: {
        bind,
        unbind,
        update
    }
};
